/**
 * Global Logic for Essenciata Boutique
 * Replaces missing theme JS to provide core functionality.
 */

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

const HTMLUpdateUtility = {
  setInnerHTML(container, html) {
    container.innerHTML = html;
    container.querySelectorAll('script').forEach(oldScript => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }
};

function focusVisiblePolyfill(container) {
  if (!container) return;
  const focusable = container.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
  if (focusable) focusable.focus();
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

// Routes
window.routes = window.routes || {
  cart_add_url: '/cart/add',
  cart_change_url: '/cart/change',
  cart_update_url: '/cart/update',
  cart_url: '/cart'
};

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.setHeaderHeight();
    }

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${document.querySelector('.section-header').offsetHeight}px`);
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      this.classList.add('active');
      this.addEventListener('transitionend', () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
        focusVisiblePolyfill(containerToTrapFocusOn);
      }, { once: true });

      document.body.classList.add('overflow-hidden');
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
    }

    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        const sectionElement = document.getElementById(section.id);
        if (sectionElement) {
          const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }
      }));

      this.classList.toggle('is-empty', parsedState.item_count === 0);
      this.open();
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer-CartItems',
          section: 'cart-drawer',
          selector: '.js-contents'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

// Modal Dialog Logic (Base for Quick Add)
if (!customElements.get('modal-dialog')) {
  class ModalDialog extends HTMLElement {
    constructor() {
      super();
      this.querySelector('[id^="ModalClose-"]').addEventListener(
        'click',
        this.hide.bind(this, false)
      );
      this.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') this.hide();
      });
      if (this.classList.contains('media-modal')) {
        this.addEventListener('pointermove', (event) => {
          if (event.pointerType === 'mouse' && (event.target.tagName === 'img' || event.target.tagName === 'video')) return;
          this.hide();
        });
      } else {
        this.addEventListener('click', (event) => {
          if (event.target === this) this.hide();
        });
      }
    }

    show(opener) {
      this.openedBy = opener;
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
    }

    hide() {
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('open');
    }
  }
  customElements.define('modal-dialog', ModalDialog);
}

// Modal Opener Logic
if (!customElements.get('modal-opener')) {
  class ModalOpener extends HTMLElement {
    constructor() {
      super();
      const button = this.querySelector('button');
      if (!button) return;
      button.addEventListener('click', () => {
        const modal = document.querySelector(this.getAttribute('data-modal'));
        if (modal) modal.show(button);
      });
    }
  }
  customElements.define('modal-opener', ModalOpener);
}

// Variant Selection Logic (Dawn Core Port)
if (!customElements.get('variant-selects')) {
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', this.onVariantChange.bind(this));
    }

    onVariantChange(event) {
      this.updateOptions();
      this.updateMasterId();
      
      // ✅ YAMPI / APPMAX FAILSAFE: Force immediately update the form inputs!
      if (this.currentVariant) {
        try {
          const sectionId = this.dataset.section;
          const inputs = document.querySelectorAll(`form[action*="/cart/add"] input[name="id"], .product-variant-id`);
          inputs.forEach((input) => {
            input.value = this.currentVariant.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
          console.log('✅ YAMPI FAILSAFE: Updated ID to', this.currentVariant.id);

          // 🚀 DIRECT PRICE ATTACK: Force update price/installments/savings
          const fetchUrl = `${window.location.pathname}?variant=${this.currentVariant.id}&section_id=${sectionId}`;
          fetch(fetchUrl)
            .then(response => response.text())
            .then(htmlText => {
               const html = new DOMParser().parseFromString(htmlText, 'text/html');
               const targets = [`price-${sectionId}`, `installment-price-${sectionId}`, `savings-badge-${sectionId}`];
               
               targets.forEach(id => {
                  const source = html.getElementById(id);
                  const destination = document.getElementById(id);
                  if (source && destination) {
                     destination.innerHTML = source.innerHTML;
                     destination.classList.remove('hidden');
                     console.log(`✅ PRICE SYNC: Updated #${id}`);
                  } else if (destination) {
                     destination.classList.add('hidden'); // Hide if not in source (like savings badge)
                  }
               });
            }).catch(e => console.warn('Price sync error:', e));

        } catch(e) { console.warn('Yampi failsafe error:', e); }
      }
      
      try {
        this.publish(event);
      } catch (err) {
        console.warn('PUB_SUB Publish Failed (Safe to ignore for checkout):', err);
      }
      
      this.forceSyncMedia();
    }

    forceSyncMedia() {
      try {
        const option1 = this.options[0];
        if (!option1) return;

        const variantData = this.getVariantData();
        if (!variantData) return;

        const normalizeValue = (val) => (val || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const option1Normalized = normalizeValue(option1);

        const sectionId = this.dataset.section;
        const script = document.querySelector(`#VariantImages-${sectionId}`);
        if (!script) return;
        
        const map = JSON.parse(script.textContent);

        // Agressive Lookup: Focus on finding the master image for the selected '1ª Fragrância'
        const targetVariant = variantData.find(v => normalizeValue(v.options[0]) === option1Normalized && map[v.id] && map[v.id] !== 'null') || this.currentVariant;

        if (targetVariant && map[targetVariant.id]) {
          const mediaId = map[targetVariant.id];
          if (mediaId && mediaId !== 'null' && mediaId !== '') {
             const gallery = document.querySelector(`media-gallery`);
             if (gallery) {
                const fullMediaId = `${sectionId}-${mediaId}`;
                const activeMedia = gallery.querySelector(`[data-media-id="${fullMediaId}"]`) || gallery.querySelector(`[data-media-id$="${mediaId}"]`);
                if (activeMedia) {
                   gallery.querySelectorAll('[data-media-id]').forEach(el => el.classList.remove('is-active'));
                   activeMedia.classList.add('is-active');
                   if (activeMedia.parentElement) {
                      setTimeout(() => {
                         activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
                      }, 50);
                   }
                }
             }
          }
        }
      } catch (err) {
         console.warn("Force sync media failed", err);
      }
    }

    updateOptions() {
      this.options = Array.from(this.querySelectorAll('fieldset'), (fieldset) => {
        return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked)?.value;
      });
      
      this.querySelectorAll('select').forEach(select => {
        if (select.value) this.options.push(select.value);
      });
      
      console.log('--- VariantSelects: Options Found ---', this.options);
    }

    updateMasterId() {
      const variantData = this.getVariantData();
      if (!variantData) return;

      this.currentVariant = variantData.find((variant) => {
        return variant.options.every((option, index) => {
          const normalizeValue = (val) => (val || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
          const selectedValue = normalizeValue(this.options[index]);
          const variantValue = normalizeValue(option);
          
          return selectedValue === variantValue;
        });
      });
      
      if (this.currentVariant) {
        console.log('--- VariantSelects: SUCCESS! Found Variant ID:', this.currentVariant.id, '---');
      } else {
        console.warn('--- VariantSelects: NO MATCH found for options:', this.options, '---');
      }
    }

    publish(event) {
      // Always publish to notify ProductInfo, even if null (to handle unavailable states)
      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target: event.target,
          selectedOptionValues: this.options,
          variant: this.currentVariant || null,
        },
      });
    }

    getVariantData() {
      if (!this.variantData) {
        const script = this.querySelector('script[id^="Variants-"]');
        if (script) {
          this.variantData = JSON.parse(script.textContent);
        } else {
          // Fallback if ID-based script is not found
          const allScripts = Array.from(this.querySelectorAll('script[type="application/json"]'));
          const dataScript = allScripts.find(s => !s.hasAttribute('data-selected-variant'));
          this.variantData = dataScript ? JSON.parse(dataScript.textContent) : [];
        }
      }
      return this.variantData;
    }
  }

  customElements.define('variant-selects', VariantSelects);
}

/**
 * SliderComponent
 * Core logic for the horizontal product gallery slider.
 */
/**
 * SliderComponent
 * Core logic for the horizontal product gallery slider.
 */
class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.currentPage = 1;

    if (!this.slider || !this.sliderItems.length) return;

    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (this.prevButton) this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    if (this.nextButton) this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    this.initPages();
    
    // Update on scroll too
    this.slider.addEventListener('scroll', debounce(this.update.bind(this), 50));
    
    const observer = new IntersectionObserver(this.update.bind(this), { 
      root: this.slider,
      threshold: 0.5 
    });
    this.sliderItems.forEach((item) => observer.observe(item));
  }

  initPages() {
    this.updateCounters();
  }

  update() {
    if (!this.slider) return;
    const sliderRect = this.slider.getBoundingClientRect();
    const activeSlide = Array.from(this.sliderItems).find((slide) => {
      const rect = slide.getBoundingClientRect();
      return rect.left >= sliderRect.left - 5 && rect.right <= sliderRect.right + 5;
    });

    if (activeSlide) {
      const newPage = Array.from(this.sliderItems).indexOf(activeSlide) + 1;
      if (this.currentPage !== newPage) {
        this.currentPage = newPage;
        this.updateCounters();
      }
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const step = event.currentTarget.name === 'next' ? 1 : -1;
    this.slideToIndex(this.currentPage + step);
  }

  slideToIndex(index) {
    if (index < 1) index = 1;
    if (index > this.sliderItems.length) index = this.sliderItems.length;

    const targetSlide = this.sliderItems[index - 1];
    if (targetSlide) {
      const left = targetSlide.offsetLeft;
      this.slider.scrollTo({
        left: left,
        behavior: 'smooth',
      });
      this.currentPage = index;
      this.updateCounters();
    }
  }

  updateCounters() {
    this.querySelectorAll('.slider-counter--current').forEach((el) => {
      el.textContent = this.currentPage;
    });
    
    if (this.prevButton) this.prevButton.disabled = this.currentPage === 1;
    if (this.nextButton) this.nextButton.disabled = this.currentPage === this.sliderItems.length;

    this.dispatchEvent(new CustomEvent('slideChanged', { 
      detail: { currentElement: this.sliderItems[this.currentPage - 1] } 
    }));
  }
}

if (!customElements.get('slider-component')) {
  customElements.define('slider-component', SliderComponent);
}

/**
 * SlideshowComponent
 * Extended slider logic to support luxury dot navigation.
 */
class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slideshow__control-wrapper');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (this.sliderControlWrapper) {
      this.sliderControlLinks = this.sliderControlWrapper.querySelectorAll('.slider-counter__link');
      this.sliderControlLinks.forEach((link) => {
        link.addEventListener('click', this.onLinkClick.bind(this));
      });
    }

    this.addEventListener('slideChanged', (event) => {
      const index = Array.from(this.sliderItems).indexOf(event.detail.currentElement);
      this.updateLinks(index + 1);
    });

    this.initAutoplay();
  }

  initAutoplay() {
    this.autoplayEnabled = this.slider.getAttribute('data-autoplay') === 'true';
    this.autoplaySpeed = parseInt(this.slider.getAttribute('data-speed')) * 1000 || 5000;

    if (this.autoplayEnabled) {
      this.startAutoplay();
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      let nextIndex = this.currentPage + 1;
      if (nextIndex > this.sliderItems.length) nextIndex = 1;
      this.slideToIndex(nextIndex);
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) clearInterval(this.autoplayTimer);
  }

  onLinkClick(event) {
    event.preventDefault();
    this.stopAutoplay();
    const index = Array.from(this.sliderControlLinks).indexOf(event.currentTarget);
    this.slideToIndex(index + 1);
    
    // Resume autoplay after manual click? Usually better to stop or have a long delay.
    // Dawn usually restarts it after a delay if mouse leaves.
  }

  updateLinks(index) {
    if (!this.sliderControlLinks) return;
    this.sliderControlLinks.forEach((link, i) => {
      if (i === index - 1) {
        link.classList.add('slider-counter__link--active');
      } else {
        link.classList.remove('slider-counter__link--active');
      }
    });
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', SlideshowComponent);
}

/**
 * ProductModal - Zoom Enhanced
 */
if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends HTMLElement {
    constructor() {
      super();
      const closeButton = this.querySelector('[id^="ModalClose-"]');
      if (closeButton) closeButton.addEventListener('click', this.hide.bind(this));
    }

    show(opener) {
      this.setAttribute('open', '');
      const mediaId = opener.getAttribute('data-media-id');
      const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`) || this.querySelector('[data-media-id]');
      
      this.querySelectorAll('[data-media-id]').forEach((element) => {
        element.style.display = 'none';
        element.classList.remove('is-active');
      });

      if (activeMedia) {
        activeMedia.style.display = 'block';
        activeMedia.classList.add('is-active');
      }
      document.body.classList.add('overflow-hidden');
    }

    hide() {
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
    }
  });
}

// Cart Drawer Opener from Header Icon
document.addEventListener('DOMContentLoaded', () => {
  const cartIcon = document.querySelector('#cart-icon-bubble');
  if (cartIcon) {
    cartIcon.addEventListener('click', (event) => {
      event.preventDefault();
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.open(cartIcon);
      } else {
        window.location.href = cartIcon.href;
      }
    });
  }
});
